// Home/Away 手動安全模式 store（APP#44）— 客戶端側「佈防 / 撤防」模式。
//
// 設計決策（triage：純客戶端、無後端依賴）：
//   - **模式本身存 client 端偏好**（localStorage，survive refresh），非後端欄位/端點；
//     沿用 lsappv3 `home_away_service.dart` 的「本地持久化 + listenable」模型，差別是
//     v3 於切換當下**真的把模式套用到裝置**（見 homeAwayApply.ts + HomeAwayControl.tsx），
//     用既有 detection op:set（0x8020/0x8041），不自創後端。
//   - 登出（signaling state 'closed'）連同 snooze/unread 一起清空——共用瀏覽器避免跨帳號
//     模式外洩（與 clearAllSnoozes 同一清理點，見 liveEvents.ts wireRegistry）。
// 純函式、無 React 依賴、好測（含 localStorage stub）。與 snooze.ts 同風格。

export type HomeAwayMode = 'home' | 'away';

const MODE_LS_KEY = 'ls.home_away.mode.v1';

/** 預設模式：在家（撤防）。首次使用 / 壞值 / SSR 皆退回此值。 */
export const DEFAULT_MODE: HomeAwayMode = 'home';

type Listener = () => void;

let mode: HomeAwayMode = load();
let version = 0;
const subs = new Set<Listener>();

function isMode(v: unknown): v is HomeAwayMode {
  return v === 'home' || v === 'away';
}

function load(): HomeAwayMode {
  try {
    const raw = globalThis.localStorage?.getItem(MODE_LS_KEY);
    return isMode(raw) ? raw : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE; // SSR / 存取被拒
  }
}

function persist(): void {
  try {
    globalThis.localStorage?.setItem(MODE_LS_KEY, mode);
  } catch {
    /* quota / SSR — 退化為記憶體內狀態 */
  }
}

function notify(): void {
  version += 1;
  for (const fn of subs) fn();
}

/** 目前安全模式。 */
export function getMode(): HomeAwayMode {
  return mode;
}

/**
 * 切換安全模式並持久化。回傳是否**真的改變**了（相同值為 no-op、不 notify、不 bump version）。
 * 注意：本函式只負責「記住偏好」；把模式套用到裝置由呼叫端以 homeAwayApply 執行。
 */
export function setMode(next: HomeAwayMode): boolean {
  if (!isMode(next) || next === mode) return false;
  mode = next;
  persist();
  notify();
  return true;
}

/** 是否處於佈防（外出）狀態。 */
export function isArmed(): boolean {
  return mode === 'away';
}

/** 全清（登出時呼叫；避免共用瀏覽器跨帳號外洩）。回到預設「在家」。 */
export function clearHomeAwayMode(): void {
  try {
    globalThis.localStorage?.removeItem(MODE_LS_KEY);
  } catch {
    /* SSR */
  }
  if (mode !== DEFAULT_MODE) {
    mode = DEFAULT_MODE;
    notify();
  }
}

/** useSyncExternalStore 快照（每次變更遞增，供 React 重繪）。 */
export function getModeVersion(): number {
  return version;
}

export function subscribeMode(fn: Listener): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}

/** 測試用：重置模組狀態。 */
export function __resetHomeAwayForTest(): void {
  mode = DEFAULT_MODE;
  version = 0;
  subs.clear();
  try {
    globalThis.localStorage?.removeItem(MODE_LS_KEY);
  } catch {
    /* SSR */
  }
}
